import { useFormContext } from 'react-hook-form';
import { useAuth, useLanguage } from '@/src/contexts';
import { TranslationKey, getText } from '@/src/helpers';
import { useAccountOffers, useSelectedTokens } from '@/src/hooks';
import { useFireblocksVaultAccount } from '@/src/hooks/apis/fireblocks';
import { OPEN_ORDERS_REFETCH_INTERVAL } from '@/src/constants';
import {
  TradingSection,
  Table,
  TableBody,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  Text,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/src/ui-components';
import { Loader } from 'lucide-react';
import { TradingFormValues } from '../../trading.types';
import { useMarketActivity } from '../market-activity/hooks';
import { OpenOrdersHeaders } from '../open-orders/components';
import { OpenOrdersBody } from '../open-orders/components/open-orders-body';
import { useHasOffersForPair } from '../open-orders/hooks';

export function ActivityAndOrders() {
  const { watch } = useFormContext<TradingFormValues>();
  const { baseTokenId, quoteTokenId } = watch();
  const [baseToken, quoteToken] = useSelectedTokens(baseTokenId, quoteTokenId);
  const { language } = useLanguage();
  const { user } = useAuth();
  const { data: vaultAccount } = useFireblocksVaultAccount();

  // Market Activity data
  const { data: transactions = [], isLoading: marketLoading, error: marketError } = useMarketActivity({
    baseToken: baseToken,
    quoteToken: quoteToken,
    limit: 10,
  });

  // Open Orders data
  const xrplAddress = user?.xrp_address;
  const {
    data: accountOffers,
    isLoading: ordersLoading,
    isError: ordersError,
    error: ordersErrorDetail,
  } = useAccountOffers(
    {
      command: 'account_offers',
      account: xrplAddress ?? '',
      ledger_index: 'validated',
    },
    {
      enabled: !!xrplAddress,
      refetchInterval: OPEN_ORDERS_REFETCH_INTERVAL,
    }
  );

  const hasOffersForPair = useHasOffersForPair(accountOffers?.offers || []);
  const getTranslation = (key: TranslationKey) => getText(language, key);

  // Market Activity helpers
  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else {
      const diffInHours = Math.floor(diffInMinutes / 60);
      return `${diffInHours}h ago`;
    }
  };

  const getTypeColor = (type: 'buy' | 'sell') => {
    return type === 'buy' ? 'text-bullish' : 'text-bearish';
  };

  const getTypeIcon = (type: 'buy' | 'sell') => {
    const iconSrc = type === 'buy' ? '/icon-buy.svg' : '/icon-sell.svg';
    return <img src={iconSrc} alt={type} className="w-4 h-4 inline-block" />;
  };

  // Market Activity content
  const marketActivityContent = marketLoading ? (
    <Empty className="w-full">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Spinner variant="circle" />
        </EmptyMedia>
        <EmptyTitle>Loading market activity</EmptyTitle>
        <EmptyDescription>
          Fetching recent transactions from XRPL network...
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  ) : marketError ? (
    <div className="flex min-h-[200px] items-center justify-center">
      <Text size="sm" className="text-red-500">Failed to fetch market activity</Text>
    </div>
  ) : baseToken?.code === 'XRP' ? (
    <div className="flex min-h-[200px] items-center justify-center">
      <Text size="sm" className="text-muted-foreground">Market activity not available for XRP</Text>
    </div>
  ) : transactions.length === 0 ? (
    <div className="flex min-h-[200px] items-center justify-center">
      <Text size="sm" className="text-muted-foreground">No recent transactions available</Text>
    </div>
  ) : (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Time</TableHead>
          <TableHead>Type</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead className="text-right">Price</TableHead>
          <TableHead className="text-right">Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions.map((tx) => (
          <TableRow key={tx.hash} className="hover:bg-gray-50 dark:hover:bg-gray-800">
            <TableCell className="text-muted-foreground p-2">{formatTime(tx.time)}</TableCell>
            <TableCell className={`font-medium ${getTypeColor(tx.type)} p-2`}>
              <span className="inline-flex items-center gap-1">
                {getTypeIcon(tx.type)}
                {tx.type.toUpperCase()}
              </span>
            </TableCell>
            <TableCell className="text-right font-mono p-2">{tx.amount}</TableCell>
            <TableCell className="text-right font-mono p-2">{tx.price}</TableCell>
            <TableCell className="text-right font-mono font-medium p-2">{tx.total}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  // Open Orders content
  let openOrdersContent: React.ReactNode;

  if (ordersLoading) {
    openOrdersContent = (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto mb-2 text-gray-900" />
          <Text size="sm" className="text-muted-foreground">Loading open orders...</Text>
        </div>
      </div>
    );
  } else if (ordersError) {
    openOrdersContent = (
      <Text size="sm" className="text-muted-foreground">
        An error occurred while fetching open orders: {ordersErrorDetail?.message || 'Unknown error'}
      </Text>
    );
  } else if (!hasOffersForPair) {
    openOrdersContent = (
      <Text size="sm" className="text-muted-foreground p-2 text-center">
        No open orders
      </Text>
    );
  } else if (!vaultAccount) {
    openOrdersContent = <Text>No vault account found</Text>;
  } else {
    const isValid = !ordersError && xrplAddress && hasOffersForPair && vaultAccount;
    if (isValid) {
      openOrdersContent = (
        <Table>
          <TableHeader>
            <OpenOrdersHeaders />
          </TableHeader>
          <TableBody>
            <OpenOrdersBody offers={accountOffers?.offers || []} vaultAccount={vaultAccount} />
          </TableBody>
        </Table>
      );
    }
  }

  return (
    <TradingSection className="flex flex-col flex-1">
      <Tabs defaultValue="orders" className="w-full flex flex-col flex-1">
        <TradingSection.Header className="pb-2">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="orders">{getTranslation('openOrders')}</TabsTrigger>
            <TabsTrigger value="activity">Market Activity</TabsTrigger>
          </TabsList>
        </TradingSection.Header>

        <TabsContent value="orders" className="mt-0 flex-1 min-h-0">
          <TradingSection.Body className="p-1 overflow-y-auto h-full">
            {openOrdersContent}
          </TradingSection.Body>
        </TabsContent>

        <TabsContent value="activity" className="mt-0 flex-1 min-h-0">
          <TradingSection.Body className="p-1 overflow-y-auto h-full">
            {marketActivityContent}
          </TradingSection.Body>
        </TabsContent>
      </Tabs>
    </TradingSection>
  );
}
